import { useState } from "react";
import { MapPin, Mail, Phone, Facebook, Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { LOCATION, SOCIALS, CONTACT_EMAIL } from "../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const buildMailto = () => {
    const subject = encodeURIComponent(
      form.subject
        ? `Nieuw bericht via Mechelen Beach website — ${form.subject}`
        : "Nieuw bericht via Mechelen Beach website",
    );
    const body = encodeURIComponent(
      `Naam: ${form.name}\nE-mail: ${form.email}\nTelefoon: ${form.phone || "—"}\nOnderwerp: ${form.subject || "—"}\n\nBericht:\n${form.message}`,
    );
    return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vul minstens je naam, e-mail en bericht in.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Verzenden mislukt.");
      }
      toast.success(
        "Bedankt voor je bericht. We nemen zo snel mogelijk contact met je op.",
      );
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(
        err.message ||
          "Er ging iets mis bij het verzenden. Probeer opnieuw of mail rechtstreeks naar mechelenbeach@gmail.com.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact | Mechelen Beach 2026"
        description="Neem contact op met de organisatie van Mechelen Beach 2026."
      />

      <section className="bg-warm-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <span className="eyebrow">Contact</span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05] max-w-3xl">
            Stuur ons een berichtje
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#6b5945] max-w-2xl">
            Heb je een vraag over Mechelen Beach 2026? Wil je een activiteit
            organiseren? Komen helpen met je vereniging? Neem gerust contact op.
            We helpen je graag verder!
          </p>
        </div>
      </section>

      <section className="bg-[#fdf8ec] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white rounded-2xl border border-[#ebd9b5] p-6 sm:p-10"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#2b1c10]">
                Vraag of voorstel?
              </h2>
              <p className="mt-2 text-sm text-[#6b5945]">
                Vul het formulier in en we komen zo snel mogelijk bij je terug.
              </p>

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-[#2b1c10]">Naam</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    data-testid="contact-input-name"
                    className="mt-2 w-full rounded-xl bg-[#fdf8ec] border border-[#ebd9b5] px-4 py-3 text-sm focus:outline-none focus:border-[#ed6a1a] focus:bg-white transition"
                    placeholder="Jouw naam"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-[#2b1c10]">E-mail</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid="contact-input-email"
                    className="mt-2 w-full rounded-xl bg-[#fdf8ec] border border-[#ebd9b5] px-4 py-3 text-sm focus:outline-none focus:border-[#ed6a1a] focus:bg-white transition"
                    placeholder="jij@voorbeeld.be"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-[#2b1c10]">Telefoon</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="contact-input-phone"
                    className="mt-2 w-full rounded-xl bg-[#fdf8ec] border border-[#ebd9b5] px-4 py-3 text-sm focus:outline-none focus:border-[#ed6a1a] focus:bg-white transition"
                    placeholder="+32 ..."
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-[#2b1c10]">Onderwerp</label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    data-testid="contact-input-subject"
                    className="mt-2 w-full rounded-xl bg-[#fdf8ec] border border-[#ebd9b5] px-4 py-3 text-sm focus:outline-none focus:border-[#ed6a1a] focus:bg-white transition"
                    placeholder="Vraag, voorstel, ..."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-[#2b1c10]">Bericht</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    data-testid="contact-input-message"
                    className="mt-2 w-full rounded-xl bg-[#fdf8ec] border border-[#ebd9b5] px-4 py-3 text-sm focus:outline-none focus:border-[#ed6a1a] focus:bg-white transition resize-none"
                    placeholder="Vertel ons wat we voor je kunnen doen..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-7 btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Versturen..." : "Stuur een bericht"}
                <ArrowRight className="h-4 w-4" />
              </button>

              {sent && (
                <p
                  data-testid="contact-success"
                  className="mt-4 text-sm text-[#1f6a2d] bg-[#eaf6ec] border border-[#cce5cf] rounded-xl px-4 py-3"
                >
                  Bedankt voor je bericht. We nemen zo snel mogelijk contact met je op.
                </p>
              )}

              <p className="mt-5 text-xs text-[#6b5945]">
                Verzenden lukt niet? Mail rechtstreeks naar{" "}
                <a
                  href={buildMailto()}
                  data-testid="contact-fallback-mailto"
                  className="font-semibold text-[#ed6a1a] hover:text-[#c54508] underline underline-offset-2"
                >
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </form>

            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden border border-[#ebd9b5] shadow-[0_18px_40px_-16px_rgba(43,28,16,0.2)]">
                <iframe
                  title="Google Maps — Mechelen Beach Douaneplein"
                  src={LOCATION.mapsEmbedUrl}
                  className="w-full h-64 sm:h-72 border-0 block"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="bg-white rounded-2xl border border-[#ebd9b5] p-6 sm:p-8">
                <span className="eyebrow">Kom langs</span>
                <h3 className="font-display mt-4 text-2xl font-semibold text-[#2b1c10]">
                  Mechelen Beach
                </h3>
                <div className="mt-5 space-y-3">
                  <a
                    href={LOCATION.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-location"
                    className="flex items-start gap-3 text-sm text-[#2b1c10] hover:text-[#ed6a1a] transition-colors"
                  >
                    <MapPin className="h-4 w-4 mt-0.5 text-[#ed6a1a] shrink-0" />
                    <span>
                      {LOCATION.name}
                      <br />
                      {LOCATION.postal} {LOCATION.city}, {LOCATION.country}
                    </span>
                  </a>
                  <a
                    href={LOCATION.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-maps-cta"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-[#ed6a1a] hover:text-[#c54508] transition-colors"
                  >
                    Open in Google Maps
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    data-testid="contact-email"
                    className="flex items-center gap-3 text-sm text-[#2b1c10] hover:text-[#ed6a1a] transition-colors"
                  >
                    <Mail className="h-4 w-4 text-[#ed6a1a]" />
                    {CONTACT_EMAIL}
                  </a>
                  <div className="flex items-center gap-3 text-sm text-[#2b1c10]">
                    <Phone className="h-4 w-4 text-[#ed6a1a]" />
                    Via formulier of social media
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#ebd9b5] p-6 sm:p-8">
                <h3 className="font-display text-xl font-semibold text-[#2b1c10]">
                  Volg Mechelen Beach
                </h3>
                <p className="mt-2 text-sm text-[#6b5945]">
                  Blijf op de hoogte van het laatste nieuws over editie 2026.
                </p>
                <div className="mt-5 flex gap-3">
                  <a
                    href={SOCIALS.facebook}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-social-facebook"
                    className="btn-dark !py-2.5 !px-5 !text-sm"
                  >
                    <Facebook className="h-4 w-4" />
                    Facebook
                  </a>
                  <a
                    href={SOCIALS.instagram}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-social-instagram"
                    className="btn-primary !py-2.5 !px-5 !text-sm"
                  >
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
